import styled from 'styled-components';

/**
 * Wrapper do react-select para controles avulsos de UI — diferente do
 * InputSelect, que é um campo de formulário ligado ao unform.
 */
const DropdownContainer = styled.div`
  /* O global.js força altura em <input>; o react-select monta um input
     invisível para acessibilidade, que precisa ficar de fora dessa regra. */
  input {
    height: unset;
  }
`;

/**
 * Os dois formatos que já existem no app: `pill` para controles de toolbar
 * (na mesma família visual dos chips de filtro) e `box` para quando o select
 * fica sob um label, alinhado aos demais campos de formulário.
 */
const VARIANTS = {
  pill: {
    height: '3.8rem',
    radius: 'var(--r-btn)',
    idleBorder: 'var(--border)',
    paddingLeft: '1.6rem',
    fontSize: '1.3rem',
    width: 'auto',
  },
  box: {
    height: '4.4rem',
    radius: 'var(--r-sm)',
    idleBorder: 'var(--border-strong)',
    paddingLeft: '1.2rem',
    fontSize: '1.4rem',
    width: '100%',
  },
};

/**
 * O react-select monta os estilos em JS, então os tokens entram como `var()`
 * — é o que mantém o controle coerente no tema claro e no escuro.
 */
const buildDropdownStyles = (variant = 'pill') => {
  const v = VARIANTS[variant] || VARIANTS.pill;

  return {
    container: styles => ({
      ...styles,
      minWidth: '18rem',
      width: v.width,
    }),
    control: (styles, { isFocused, menuIsOpen }) => ({
      ...styles,
      minHeight: v.height,
      height: v.height,
      paddingLeft: v.paddingLeft,
      paddingRight: '0.6rem',
      borderRadius: v.radius,
      backgroundColor: 'var(--surface)',
      border:
        isFocused || menuIsOpen
          ? '1px solid var(--accent)'
          : `1px solid ${v.idleBorder}`,
      boxShadow: isFocused ? '0 0 0 3px var(--accent-tint)' : 'none',
      cursor: 'pointer',
      transition: 'border-color var(--dur-fast) var(--ease-standard)',
      ':hover': {
        borderColor:
          isFocused || menuIsOpen ? 'var(--accent)' : 'var(--border-strong)',
      },
    }),
    valueContainer: styles => ({
      ...styles,
      padding: 0,
    }),
    singleValue: styles => ({
      ...styles,
      margin: 0,
      fontSize: v.fontSize,
      fontWeight: 500,
      color: 'var(--ink)',
    }),
    placeholder: styles => ({
      ...styles,
      margin: 0,
      fontSize: v.fontSize,
      color: 'var(--ink-faint)',
    }),
    input: styles => ({
      ...styles,
      margin: 0,
      padding: 0,
      color: 'var(--ink)',
    }),
    indicatorSeparator: () => ({ display: 'none' }),
    dropdownIndicator: (styles, { selectProps }) => ({
      ...styles,
      /* Era aqui que o chevron encostava na borda: o <select> nativo desenha a
         seta colada na direita e o padding do CSS não a alcança. */
      padding: '0 0.8rem',
      color: 'var(--ink-faint)',
      transform: selectProps.menuIsOpen ? 'rotate(180deg)' : 'none',
      transition: 'transform var(--dur-fast) var(--ease-standard)',
      ':hover': { color: 'var(--ink)' },
    }),
    menu: styles => ({
      ...styles,
      marginTop: '0.6rem',
      minWidth: '100%',
      width: 'max-content',
      right: 0,
      backgroundColor: 'var(--surface)',
      border: '1px solid var(--border)',
      borderRadius: 'var(--r-sm)',
      boxShadow: 'var(--shadow-md)',
      overflow: 'hidden',
      zIndex: 5,
    }),
    menuList: styles => ({
      ...styles,
      padding: '0.4rem',
    }),
    option: (styles, { isFocused, isSelected }) => {
      let backgroundColor = 'transparent';
      if (isSelected) backgroundColor = 'var(--accent-tint)';
      else if (isFocused) backgroundColor = 'var(--surface-2)';

      return {
        ...styles,
        backgroundColor,
        color: isSelected ? 'var(--accent-deep)' : 'var(--ink)',
        fontSize: v.fontSize,
        fontWeight: isSelected ? 600 : 500,
        padding: '0.9rem 1.2rem',
        borderRadius: 'var(--r-xs)',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        ':active': { backgroundColor: 'var(--accent-tint)' },
      };
    },
  };
};

export { DropdownContainer, buildDropdownStyles, VARIANTS };
